import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import BusinessOpportunities from "@/components/pawkt/BusinessOpportunities";
import { BusinessActionsCard } from "@/components/dashboard/BusinessActionsCard";
import {
  CalendarDays, AlertTriangle, Sparkles, Lightbulb, Activity, ArrowRight,
  Plus, UserPlus, PawPrint, FileText, MessageCircle, Building2, ChevronDown,
  TrendingUp, TrendingDown, Minus, Clock, Wallet, Crown, Repeat, Bell,
  CheckCircle2, ShieldAlert, Route, GitBranch,
} from "lucide-react";

/* ---- Pawkt design tokens (per spec) ---- */
const C = {
  bg: "#FAF8F5", surface: "#FFFFFF", elevated: "#F5F1EA",
  primary: "#7C5CFC", primaryDeep: "#5837D8", primarySoft: "#EEE9FE", primaryLight: "#B8A5FD",
  secondary: "#F7CDBA", text: "#1F1B18", textSec: "#7B6D62", textDis: "#B5A89D",
  border: "#EEE8DD", borderStrong: "#DFD6C6", divider: "#F0E8DA",
  urgent: "#C8482A", soon: "#B8791F", info: "#7C5CFC",
};
const money = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
const compactMoney = (v) => {
  v = Math.round(v || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.00$/, "")}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `₹${v.toLocaleString("en-IN")}`;
};
const greetWord = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};
const firstName = (n) => (n || "").trim().split(/\s+/)[0] || "there";

const QA = {
  new_booking: { icon: CalendarDays, to: "/bookings" },
  add_customer: { icon: UserPlus, to: "/customers" },
  add_pet: { icon: PawPrint, to: "/pets" },
  create_invoice: { icon: FileText, to: "/invoices" },
  ask_shally: { icon: MessageCircle, to: "/dashboard" },
};

/* priority → human severity (never colour-only: icon + word) */
const SEV = {
  high: { word: "Urgent", color: C.urgent, Icon: ShieldAlert },
  medium: { word: "Soon", color: C.soon, Icon: Clock },
  low: { word: "FYI", color: C.textSec, Icon: Bell },
};

const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

/* strip server-side WHAT/WHY/EVIDENCE/SOURCE scaffolding → plain business sentence */
const cleanDesc = (s) => {
  if (!s) return "";
  const t = String(s);
  const m = t.match(/WHAT:\s*([\s\S]*?)(?:\s*(?:WHY:|EVIDENCE:|SOURCE:)|$)/);
  if (m && m[1].trim()) return m[1].trim();
  return t.split(/\s*(?:WHY:|EVIDENCE:|SOURCE:)/)[0].trim();
};

const issueLine = (it) => {
  if (it.rule === "outstanding_payment") return `${money(it.balance || 0)} outstanding${it.invoice_number ? ` · ${it.invoice_number}` : ""}`;
  if (it.rule === "membership_expiring") return "Membership renewal approaching";
  return it.label;
};
const deadlineLabel = (d) => (d.days <= 0 ? "Due today" : `${d.days} day${d.days === 1 ? "" : "s"} left`);

/* ---------- small building blocks (sparing card use) ---------- */
const Band = ({ id, title, Icon, action, children, muted }) => (
  <section className="mb-10" data-testid={`cc-section-${id}`}>
    <div className="flex items-center justify-between mb-3">
      <h2 className="flex items-center gap-2" style={{ color: C.text, fontSize: "0.8125rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {Icon && <Icon size={15} style={{ color: muted ? C.textDis : C.primary }} />} {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const LinkBtn = ({ to, children, testid, variant = "ghost" }) => {
  const nav = useNavigate();
  const base = "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold transition-colors";
  const styles = variant === "solid"
    ? { background: C.primary, color: "#fff" }
    : { background: C.surface, color: C.primaryDeep, border: `1px solid ${C.primaryLight}` };
  return (
    <button data-testid={testid} onClick={() => nav(to)} className={base} style={styles}
      onMouseEnter={(e) => { if (variant !== "solid") e.currentTarget.style.background = C.primarySoft; }}
      onMouseLeave={(e) => { if (variant !== "solid") e.currentTarget.style.background = C.surface; }}>
      {children}
    </button>
  );
};

/* ================================================================== */
export default function CommandCenter() {
  const nav = useNavigate();
  const { user } = useAuth();
  const branches = user?.branches || [];
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAllToday, setShowAllToday] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const branchRef = useRef(null);
  const [assignees, setAssignees] = useState([]);
  const [assignSel, setAssignSel] = useState({});
  const [approving, setApproving] = useState(null);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [expanded, setExpanded] = useState({});
  const canApprove = hasPerm(user, "communications.send");

  useEffect(() => {
    if (!canApprove) return;
    api.get("/command-center/assignees").then((r) => setAssignees(r.data.staff || [])).catch(() => {});
  }, [canApprove]);

  useEffect(() => {
    const h = (e) => { if (branchRef.current && !branchRef.current.contains(e.target)) setBranchOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
    api.get(`/command-center${qs}`)
      .then((r) => { if (alive) { setData(r.data); setErr(""); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(apiErr(e)); setLoading(false); } });
    return () => { alive = false; };
  }, [branchId]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="cc-loading" style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
        <div className="h-28 rounded-[18px]" style={{ background: C.elevated }} />
        <div className="h-40 rounded-[18px]" style={{ background: C.elevated }} />
        <div className="h-40 rounded-[18px]" style={{ background: C.elevated }} />
      </div>
    );
  }
  if (err) return <div className="p-8 rounded-[18px]" data-testid="cc-error" style={{ color: C.urgent, background: C.surface, border: `1px solid ${C.border}` }}>{err}</div>;

  const w = data.business_weather || {};
  const trend = data.trend || {};
  const needsSummary = data.needs_summary || { total: 0, categories: [] };
  const needsGroups = data.needs_groups || { customers: [], total_customers: 0, more_customers: 0 };
  const recsSummary = data.recommendations_summary || { total: 0, categories: [] };
  const recs = data.recommendations || [];
  const approveRec = async (r) => {
    setApproving(r.id);
    try {
      await api.post(`/recommendations/${r.id}/approve`, { assigned_staff_id: assignSel[r.id] || "" });
      setApprovedIds((p) => new Set(p).add(r.id));
      toast.success(assignSel[r.id] ? "Approved and assigned." : "Approved — Pawkt created the follow-up.");
    } catch (e) {
      if (e?.response?.status === 409) { setApprovedIds((p) => new Set(p).add(r.id)); toast.message("Already handled — this is no longer needed."); }
      else toast.error(apiErr(e));
    } finally { setApproving(null); }
  };
  const preds = data.predictions || [];
  const act = data.automation_activity || {};
  const insight = data.shally_insight;
  const today = data.today || { bookings: [], counts: {} };
  const bg = data.business_goal;
  const fin = data.financial_pulse;
  const finsig = data.financial_signals;
  const rsig = data.resource_signals;
  const sub = data.subsystems || {};
  const fact = (label) => (w.facts || []).find((f) => (f.label || "").toLowerCase().startsWith(label.toLowerCase()))?.value || 0;
  const collected = fact("Collected");
  const TrendIcon = trend.direction === "up" ? TrendingUp : trend.direction === "down" ? TrendingDown : Minus;

  return (
    <div data-testid="command-center" style={{ fontFamily: "'Nunito Sans', sans-serif", color: C.text }}>
      {/* ---------------- BUSINESS PULSE (calm, not a KPI wall) ---------------- */}
      <header className="mb-10" data-testid="cc-section-business-pulse">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p style={{ color: C.textSec, fontSize: "0.9375rem", fontWeight: 600 }}>
              {greetWord()}, {firstName(user?.name)} <span aria-hidden>👋</span>
            </p>
            <h1 className="mt-1" style={{ fontSize: "1.75rem", lineHeight: 1.15, fontWeight: 800, maxWidth: "34ch" }} data-testid="cc-pulse-headline">
              {w.headline}
            </h1>
          </div>
          {branches.length > 1 && (
            <div className="relative" ref={branchRef} data-testid="cc-branch-switcher">
              <button onClick={() => setBranchOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={branchOpen}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold"
                style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text }}>
                <Building2 size={15} style={{ color: C.textDis }} />
                {branchId ? (branches.find((b) => b.id === branchId)?.name || "Branch") : "All branches"}
                <ChevronDown size={15} style={{ color: C.textDis }} />
              </button>
              {branchOpen && (
                <ul role="listbox" className="absolute right-0 z-20 mt-2 w-56 rounded-2xl p-1.5"
                  style={{ background: C.surface, border: `1px solid ${C.border}`, boxShadow: "0 12px 30px rgba(31,27,24,0.12)" }}>
                  {[{ id: "", name: "All branches" }, ...branches].map((b) => (
                    <li key={b.id || "all"}>
                      <button data-testid={`cc-branch-opt-${b.id || "all"}`} role="option" aria-selected={branchId === b.id}
                        onClick={() => { setBranchId(b.id); setBranchOpen(false); }}
                        className="w-full rounded-xl px-3 py-2 text-left text-sm font-semibold"
                        style={{ color: C.text, background: branchId === b.id ? C.primarySoft : "transparent" }}>
                        {b.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* inline factual signals — not cards */}
        {w.state !== "insufficient_data" ? (
          <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="cc-pulse-signals">
            <Signal value={today.counts?.total || 0} label="appointments today" />
            <Signal value={compactMoney(collected)} label="collected this month" />
            <Signal value={needsSummary.total} label="need your attention" accent={needsSummary.total > 0} />
            <div className="inline-flex items-center gap-1.5" style={{ color: trend.direction === "down" ? C.urgent : C.textSec }}>
              <TrendIcon size={16} />
              <span className="text-sm font-semibold" data-testid="cc-pulse-trend">{trend.label}</span>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm" style={{ color: C.textSec }} data-testid="cc-pulse-empty">
            {w.detail || "Keep using Pawkt. Your business picture will become richer as your activity grows."}
          </p>
        )}

        {/* quick actions */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="cc-quick-actions">
          {(data.quick_actions || []).map((a) => {
            const Icon = QA[a.key]?.icon || Plus;
            return (
              <button key={a.key} data-testid={`cc-qa-${a.key}`} onClick={() => nav(QA[a.key]?.to || "/dashboard")}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors"
                style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.primaryLight; e.currentTarget.style.background = C.primarySoft; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}>
                <Icon size={15} style={{ color: C.primary }} /> {a.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* ---------------- FINANCIAL PULSE (flag profit_intelligence) ---------------- */}
      <div className="mb-10" data-testid="cc-section-business-actions"><BusinessActionsCard variant="command" /></div>
      {fin && (
        <section className="mb-10" data-testid="cc-section-financial-pulse">
          <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="cc-financial-pulse">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.textDis }}>Financial pulse · recorded data</span>
              <span data-testid="cc-fin-quality" className="rounded-full px-2.5 py-0.5 text-[11px] font-bold"
                style={fin.expenses_quality === "COMPLETE" ? { background: C.primarySoft, color: C.primaryDeep } : { background: "#FBF3E8", color: C.soon }}>
                Expense data: {fin.expenses_quality}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-7 gap-y-2">
              <Signal value={compactMoney(fin.collected)} label="collected" />
              <Signal value={compactMoney(fin.billed)} label="billed" />
              <Signal value={compactMoney(fin.outstanding)} label="outstanding" accent={fin.outstanding > 0} />
              <Signal value={compactMoney(fin.recorded_costs)} label="recorded costs" />
              <Signal value={compactMoney(fin.recorded_contribution)} label={fin.contribution_status === "complete" ? "operating contribution" : "recorded contribution"} />
            </div>
            {fin.contribution_status !== "complete" && fin.top_warning && (
              <div className="mt-3 rounded-[12px] p-3 text-xs" style={{ background: C.elevated, color: C.textSec }} data-testid="cc-fin-note">
                {fin.top_warning}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---------------- FINANCIAL SIGNALS (flag profit_intelligence_analysis) ---------------- */}
      {finsig && (finsig.signals || []).length > 0 && (
        <section className="mb-10" data-testid="cc-section-financial-signals">
          <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="cc-financial-signals">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.textDis }}>Financial signals · vs {finsig.comparison_period}</span>
              <a href="/business-planning" className="text-[11px] font-bold" style={{ color: C.primaryDeep }} data-testid="cc-fin-signals-details">View details →</a>
            </div>
            <div className="mt-2 text-sm font-semibold" style={{ color: C.text }} data-testid="cc-fin-signals-headline">{finsig.headline}</div>
            <div className="mt-3 space-y-2">
              {finsig.signals.map((s, i) => (
                <div key={i} className="flex items-start gap-2 rounded-[12px] p-3" style={{ background: C.elevated }} data-testid={`cc-fin-signal-${i}`}>
                  <span className="mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                    style={s.severity === "CRITICAL" ? { background: "#FCE8E8", color: "#B4231F" } : { background: "#FBF3E8", color: C.soon }}>
                    {s.severity}
                  </span>
                  <span className="flex-1 text-xs" style={{ color: C.textSec }}>{s.message}</span>
                  <a href="/business-planning" data-testid={`cc-fin-signal-review-${i}`} className="shrink-0 text-[11px] font-bold" style={{ color: C.primaryDeep }}>Review</a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- RESOURCE & COST SIGNALS (flag profit_intelligence_resources) ---------------- */}
      {rsig && (rsig.signals || []).length > 0 && (
        <section className="mb-10" data-testid="cc-section-resource-signals">
          <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="cc-resource-signals">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.textDis }}>Resource &amp; cost signals · vs {rsig.comparison_period}</span>
              <a href="/business-planning" className="text-[11px] font-bold" style={{ color: C.primaryDeep }} data-testid="cc-resource-signals-details">View details →</a>
            </div>
            <div className="mt-2 text-xs" style={{ color: C.textDis }} data-testid="cc-resource-capacity-note">Capacity utilization: {rsig.capacity_utilization}</div>
            <div className="mt-3 space-y-2">
              {rsig.signals.map((s, i) => (
                <div key={i} className="flex items-start gap-2 rounded-[12px] p-3" style={{ background: C.elevated }} data-testid={`cc-resource-signal-${i}`}>
                  <span className="mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                    style={s.severity === "CRITICAL" ? { background: "#FCE8E8", color: "#B4231F" } : { background: "#FBF3E8", color: C.soon }}>
                    {s.severity}
                  </span>
                  <span className="flex-1 text-xs" style={{ color: C.textSec }}>{s.message}</span>
                  <a href="/business-planning" data-testid={`cc-resource-signal-review-${i}`} className="shrink-0 text-[11px] font-bold" style={{ color: C.primaryDeep }}>Review</a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {bg && bg.has_goal && (
        <section className="mb-10" data-testid="cc-section-business-goal">
          <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="cc-business-goal">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.textDis }}>{bg.period} · Revenue goal</div>
                <div className="mt-0.5 font-display" style={{ fontSize: "1.6rem", fontWeight: 800, color: C.text }} data-testid="cc-goal-target">{compactMoney(bg.target)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span data-testid="cc-goal-status" className="rounded-full px-3 py-1 text-xs font-bold"
                  style={bg.status === "behind" ? { background: "#FBF3E8", color: C.soon } : { background: C.primarySoft, color: C.primaryDeep }}>
                  {bg.status === "behind" ? "Behind pace" : bg.status === "achieved" ? "Achieved" : bg.status === "period_ended" ? "Period ended" : "On / ahead of pace"}
                </span>
                <LinkBtn to="/business-planning" testid="cc-goal-open">Open plan</LinkBtn>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-2.5 w-full overflow-hidden rounded-full" style={{ background: C.elevated }}>
                <div className="h-full rounded-full" style={{ width: `${Math.min(100, bg.pct_achieved)}%`, background: C.primary }} data-testid="cc-goal-bar" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-8 gap-y-2">
                <Signal value={compactMoney(bg.actual_revenue)} label="actual revenue" />
                <Signal value={compactMoney(bg.remaining)} label="remaining to target" accent={bg.remaining > 0} />
                <Signal value={`${compactMoney(bg.required_pace)}/day`} label="required pace" />
                <Signal value={`${compactMoney(bg.current_pace)}/day`} label="current pace" />
                <Signal value={compactMoney(bg.potential_additional_revenue)} label="potential (estimate)" />
              </div>
              {(bg.department_gaps || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="cc-goal-gaps">
                  {bg.department_gaps.map((d) => (
                    <span key={d.category} className="rounded-full px-3 py-1 text-xs font-semibold capitalize"
                      style={{ background: C.elevated, color: C.textSec }}>{d.category}: {compactMoney(d.gap)} gap</span>
                  ))}
                </div>
              )}
              {bg.plan && (
                <div className="mt-4 rounded-[14px] p-4" style={{ background: C.elevated }} data-testid="cc-goal-plan">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.textDis }}>Plan · {bg.plan.title}</span>
                    <span data-testid="cc-plan-status" className="rounded-full px-2.5 py-0.5 text-[11px] font-bold"
                      style={bg.plan.status === "behind" ? { background: "#FBEAEA", color: "#A33" } : bg.plan.status === "at_risk" ? { background: "#FBF3E8", color: C.soon } : { background: C.primarySoft, color: C.primaryDeep }}>
                      {bg.plan.status === "on_track" ? "On track" : bg.plan.status === "at_risk" ? "At risk" : bg.plan.status === "behind" ? "Behind" : bg.plan.status}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-7 gap-y-2">
                    <Signal value={compactMoney(bg.plan.planned_contribution)} label="planned" />
                    <Signal value={compactMoney(bg.plan.observed_contribution)} label="observed" />
                    <Signal value={compactMoney(bg.plan.remaining_uncovered)} label="uncovered" accent={bg.plan.remaining_uncovered > 0} />
                    <Signal value={`${bg.plan.actions?.completed || 0}/${bg.plan.actions?.total || 0}`} label="actions done" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- NEEDS YOU — grouped, prioritized (calm, not 200 cards) ---------------- */}
      <Band id="needs-you" title="Needs You" Icon={AlertTriangle}
        action={needsSummary.total > 0 && <LinkBtn to="/action-center" testid="cc-needs-view-all">View all {needsSummary.total}</LinkBtn>}>
        {sub.opportunities === "unavailable" ? (
          <Notice>Some attention items are temporarily unavailable.</Notice>
        ) : null}
        {needsSummary.total > 0 ? (
          <>
            <p className="mb-3 text-sm" style={{ color: C.textSec }} data-testid="cc-needs-total">
              <span className="font-extrabold" style={{ color: C.text }}>{needsSummary.total}</span> {needsSummary.total === 1 ? "thing needs" : "things need"} your attention
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4" data-testid="cc-needs-groups">
              {needsSummary.categories.map((g) => {
                const sev = SEV[g.priority] || SEV.medium;
                return (
                  <button key={g.key} data-testid={`cc-needs-cat-${g.key}`} onClick={() => nav(g.nav)}
                    className="rounded-[18px] p-4 text-left transition-colors"
                    style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sev.color}` }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.primaryLight)}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-extrabold text-xl" style={{ color: C.text }}>{g.count}</span>
                      <span className="text-sm font-semibold" style={{ color: C.text }}>{g.label}</span>
                    </div>
                    <p className="mt-0.5 text-[13px]" style={{ color: C.textSec }}>
                      {g.value > 0 ? `${compactMoney(g.value)} in play` : "Ready to act on"}
                    </p>
                  </button>
                );
              })}
            </div>
            {needsGroups.customers.length > 0 && (
              <div className="space-y-2.5" data-testid="cc-needs-rollups">
                {needsGroups.customers.map((cg) => {
                  const sev = SEV[cg.priority] || SEV.medium;
                  const open = !!expanded[cg.customer_id];
                  return (
                    <article key={cg.customer_id} data-testid={`cc-rollup-${cg.customer_id}`} className="rounded-[18px] overflow-hidden"
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sev.color}` }}>
                      <button data-testid={`cc-rollup-toggle-${cg.customer_id}`} onClick={() => setExpanded((s) => ({ ...s, [cg.customer_id]: !open }))}
                        className="w-full flex items-center justify-between gap-3 p-5 text-left">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold" style={{ color: C.text, fontSize: "0.9375rem" }}>{cg.customer_name}</span>
                            <span className="text-[11px] font-extrabold uppercase tracking-wide" style={{ color: sev.color }}>{sev.word}</span>
                          </div>
                          <p className="mt-0.5 text-[13px]" style={{ color: C.textSec }}>{cg.count} {cg.count === 1 ? "thing needs" : "things need"} attention</p>
                        </div>
                        <ChevronDown size={18} style={{ color: C.textSec, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                      </button>
                      {open && (
                        <div className="px-5 pb-5 space-y-2" data-testid={`cc-rollup-body-${cg.customer_id}`}>
                          {cg.issues.map((it, idx) => (
                            <button key={idx} data-testid={`cc-rollup-issue-${cg.customer_id}-${idx}`} onClick={() => nav(it.nav)}
                              className="w-full flex items-center justify-between gap-2 rounded-xl p-3 text-left" style={{ background: C.elevated }}>
                              <span className="min-w-0 text-sm font-semibold truncate" style={{ color: C.text }}>
                                {issueLine(it)}{it.pet ? <span className="font-normal" style={{ color: C.textSec }}> · {it.pet}</span> : null}
                              </span>
                              {it.deadline && (
                                <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold"
                                  style={{ background: it.deadline.urgent ? "#FDEBE8" : C.primarySoft, color: it.deadline.urgent ? C.urgent : C.primaryDeep }}>
                                  <Clock size={11} /> {deadlineLabel(it.deadline)}
                                </span>
                              )}
                            </button>
                          ))}
                          <div className="pt-1">
                            <LinkBtn to={cg.nav} testid={`cc-rollup-review-${cg.customer_id}`} variant="solid">Review Customer <ArrowRight size={14} /></LinkBtn>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
                {needsGroups.more_customers > 0 && (
                  <p className="text-[13px]" style={{ color: C.textSec }} data-testid="cc-rollup-more">
                    +{needsGroups.more_customers} more {needsGroups.more_customers === 1 ? "customer needs" : "customers need"} attention · <button onClick={() => nav("/action-center")} className="font-bold underline" style={{ color: C.primaryDeep }}>View all</button>
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <EmptyRow Icon={CheckCircle2} testid="cc-needs-empty">You're all caught up — nothing needs your attention right now.</EmptyRow>
        )}
      </Band>

      {/* ---------------- TODAY AT PAWKT ---------------- */}
      <Band id="today-at-pawkt" title="Today at Pawkt" Icon={CalendarDays}
        action={today.bookings?.length > 0 && <LinkBtn to="/calendar" testid="cc-today-view-schedule">View schedule</LinkBtn>}>
        <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mb-4">
            <Signal value={today.counts?.total || 0} label="appointments" />
            {today.expected_collections > 0 && <Signal value={compactMoney(today.expected_collections)} label="expected to collect" />}
          </div>
          {today.bookings?.length ? (
            <ol className="relative" data-testid="cc-today-list">
              {(showAllToday ? today.bookings : today.bookings.slice(0, 5)).map((b) => (
                <li key={b.id} className="flex items-center gap-4 py-2.5" style={{ borderTop: `1px solid ${C.divider}` }}>
                  <span className="text-[13px] font-bold tabular-nums w-14 shrink-0" style={{ color: C.primaryDeep }}>
                    {b.start_time ? new Date(b.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "--:--"}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-sm font-semibold" style={{ color: C.text }}>
                    {b.pet_name || b.customer_name || "Appointment"}
                    {b.service_name && <span className="font-normal" style={{ color: C.textSec }}> · {b.service_name}</span>}
                  </span>
                  <StatusPill status={b.status} />
                </li>
              ))}
              {today.bookings.length > 5 && (
                <button onClick={() => setShowAllToday((s) => !s)} data-testid="cc-today-toggle"
                  className="mt-3 text-[13px] font-bold" style={{ color: C.primaryDeep }}>
                  {showAllToday ? "Show less" : `View all ${today.bookings.length} appointments`}
                </button>
              )}
            </ol>
          ) : <EmptyRow Icon={CalendarDays} testid="cc-today-empty">No appointments scheduled today.</EmptyRow>}
        </div>
      </Band>

      {/* ---------------- IN PROGRESS — workflow + care journeys (business meaning) ---------------- */}
      <OperationsBand ops={data.operations} nav={nav} />

      {/* ---------------- PAWKT SIGNAL (predictions: active + surfaceable only) ---------------- */}
      {preds.length > 0 && (
        <Band id="pawkt-signal" title="Pawkt Signal" Icon={Sparkles}>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="cc-signal-grid">
            {preds.slice(0, 4).map((p) => <PredictionCard key={p.id} p={p} />)}
          </div>
        </Band>
      )}

      {/* ---------------- PREDICTED OPPORTUNITIES (business propensity, flag-gated) ---------------- */}
      <BusinessOpportunities scope="command" />

      {/* ---------------- RECOMMENDATIONS (distinct from opportunities) ---------------- */}
      <Band id="recommendations" title="Pawkt Recommends" Icon={Lightbulb}
        action={recsSummary.total > 5 && <LinkBtn to="/action-center" testid="cc-recs-view-all">View all {recsSummary.total}</LinkBtn>}>
        {sub.recommendations === "unavailable" ? (
          <Notice>Recommendations are temporarily unavailable.</Notice>
        ) : recs.length ? (
          <div className="space-y-2.5" data-testid="cc-recs-list">
            {recs.filter((r) => !approvedIds.has(r.id)).slice(0, 5).map((r) => (
              <article key={r.id} data-testid={`cc-rec-${r.id}`}
                className="rounded-[18px] p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                style={{ background: C.primarySoft, border: `1px solid ${C.primaryLight}` }}>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm" style={{ color: C.text }}>{cleanDesc(r.description)}</h3>
                  {(r.reason_codes || []).length > 0 && (
                    <p className="mt-1 text-[13px]" style={{ color: C.textSec }}>
                      Why? {r.reason_codes.map(humanize).join(" · ")}
                    </p>
                  )}
                  {r.requires_confirmation && (
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: C.soon }}>
                      <ShieldAlert size={12} /> Needs your confirmation before anything happens
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {canApprove && (r.action_type === "CREATE_FOLLOWUP" || r.action_type === "CREATE_TASK") && assignees.length > 0 && (
                    <select data-testid={`cc-rec-assign-${r.id}`} value={assignSel[r.id] || ""}
                      onChange={(e) => setAssignSel((s) => ({ ...s, [r.id]: e.target.value }))}
                      className="rounded-lg px-2 py-1.5 text-[13px] font-semibold"
                      style={{ background: C.surface, border: `1px solid ${C.primaryLight}`, color: C.text }}>
                      <option value="">Assign to… (optional)</option>
                      {assignees.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                  {canApprove && (
                    <button data-testid={`cc-rec-approve-${r.id}`} disabled={approving === r.id} onClick={() => approveRec(r)}
                      className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-[13px] font-bold text-white disabled:opacity-50"
                      style={{ background: C.primary }}>
                      <CheckCircle2 size={14} /> {approving === r.id ? "Approving…" : "Approve"}
                    </button>
                  )}
                  <LinkBtn to={r.customer_id ? `/customers/${r.customer_id}` : "/action-center"} testid={`cc-rec-review-${r.id}`} variant={canApprove ? "ghost" : "solid"}>Review</LinkBtn>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyRow Icon={Lightbulb} testid="cc-recs-empty">Nothing needs action right now.</EmptyRow>
        )}
      </Band>

      {/* ---------------- SHALLY + PAWKT DID THIS ---------------- */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Band id="shally" title="Ask Shally" Icon={MessageCircle}>
          <div className="rounded-[18px] p-5" style={{ background: C.elevated, border: `1px solid ${C.borderStrong}` }} data-testid="cc-shally">
            {insight ? (
              <div className="mb-4 rounded-[14px] p-3.5" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                <p className="text-[11px] font-extrabold uppercase tracking-wide mb-1" style={{ color: C.primary }}>Shally insight</p>
                <p className="text-[13px]" style={{ color: C.text }}>{insight.insight}</p>
                <p className="mt-1.5 text-[11px]" style={{ color: C.textDis }}>Grounded in {humanize(insight.evidence_source)}</p>
              </div>
            ) : (
              <p className="mb-4 text-[13px]" style={{ color: C.textSec }} data-testid="cc-shally-empty">Shally doesn't have a meaningful new insight yet.</p>
            )}
            <div className="flex flex-col gap-1.5">
              {["What needs my attention today?", "What are my best opportunities?", "How much am I expected to collect?"].map((q, i) => (
                <button key={i} onClick={() => nav("/dashboard")} data-testid={`cc-shally-q-${i}`}
                  className="text-left text-[13px] font-semibold rounded-xl px-3 py-2 transition-colors"
                  style={{ color: C.primaryDeep, background: C.surface, border: `1px solid ${C.border}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.primarySoft)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.surface)}>
                  “{q}”
                </button>
              ))}
            </div>
            <button onClick={() => nav("/dashboard")} data-testid="cc-ask-shally-btn"
              className="mt-3 w-full rounded-full py-2.5 text-sm font-bold" style={{ background: C.primary, color: "#fff" }}>
              Ask Shally
            </button>
          </div>
        </Band>

        <Band id="automation-activity" title="Pawkt Did This" Icon={Activity} muted
          action={<LinkBtn to="/action-center" testid="cc-activity-view">View activity</LinkBtn>}>
          <div className="rounded-[18px] p-5" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="cc-activity">
            <ul className="space-y-1.5 text-sm" style={{ color: C.text }}>
              <ActivityRow n={act.followups_created || 0} label="follow-ups created" />
              <ActivityRow n={act.tasks_created || 0} label="tasks assigned" />
              <ActivityRow n={act.executed || 0} label="automated actions completed" />
              {(act.failed || 0) > 0 && <ActivityRow n={act.failed} label="actions failed" bad />}
            </ul>
            <DeliveryBreakdown counts={act.delivery_counts} suppressed={act.suppressed_examples} />
            {act.note && <p className="mt-3 text-[11px] leading-relaxed" style={{ color: C.textDis }}>{act.note}</p>}
          </div>
        </Band>
      </div>
    </div>
  );
}

/* ----------------------------- sub-components ----------------------------- */
const Signal = ({ value, label, accent }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="font-extrabold" style={{ color: accent ? "#C8482A" : "#1F1B18", fontSize: "1.375rem", lineHeight: 1 }}>{value}</span>
    <span className="text-[13px]" style={{ color: "#7B6D62" }}>{label}</span>
  </div>
);

const Notice = ({ children }) => (
  <div className="mb-3 rounded-xl px-4 py-2.5 text-[13px] font-semibold flex items-center gap-2"
    style={{ background: "#FBF3E8", border: "1px solid #E9D6B8", color: "#8A6A1F" }} data-testid="cc-partial-notice">
    <AlertTriangle size={14} /> {children}
  </div>
);

const EmptyRow = ({ Icon, children, testid }) => (
  <div className="rounded-[18px] p-6 flex items-center gap-3" style={{ background: "#FFFFFF", border: "1px dashed #DFD6C6" }} data-testid={testid}>
    <Icon size={18} style={{ color: "#B5A89D" }} />
    <p className="text-sm" style={{ color: "#7B6D62" }}>{children}</p>
  </div>
);

const STATUS = {
  confirmed: { t: "Confirmed", c: "#2F8F5B", bg: "#E7F4EC" },
  completed: { t: "Completed", c: "#2F8F5B", bg: "#E7F4EC" },
  pending: { t: "Pending", c: "#B8791F", bg: "#FBF3E8" },
  in_progress: { t: "In progress", c: "#5837D8", bg: "#EEE9FE" },
  cancelled: { t: "Cancelled", c: "#7B6D62", bg: "#F0E8DA" },
  no_show: { t: "No show", c: "#C8482A", bg: "#FBEAE4" },
};
const StatusPill = ({ status }) => {
  const s = STATUS[status] || { t: humanize(status || "—"), c: "#7B6D62", bg: "#F0E8DA" };
  return <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ color: s.c, background: s.bg }}>{s.t}</span>;
};

function PredictionCard({ p }) {
  const nav = useNavigate();
  const kind = p.prediction_type === "CUSTOMER_RETURN_WINDOW"
    ? "A customer is approaching their usual return window."
    : "A pet is approaching its usual service window.";
  const from = p.predicted_date_from ? new Date(p.predicted_date_from) : null;
  const to = p.predicted_date_to ? new Date(p.predicted_date_to) : null;
  const fmt = (d) => d?.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const windowTxt = from && to ? `Expected around ${fmt(from)} – ${fmt(to)}` : from ? `Expected around ${fmt(from)}` : null;
  const conf = (p.confidence_level || "").toLowerCase();
  return (
    <article data-testid={`cc-signal-${p.id}`} className="rounded-[18px] p-5 flex flex-col"
      style={{ background: "#FFFFFF", border: "1px solid #EEE8DD", borderLeft: "4px solid #7C5CFC" }}>
      <div className="flex items-center gap-1.5 mb-2" style={{ color: "#5837D8" }}>
        <Sparkles size={14} />
        <span className="text-[11px] font-extrabold uppercase tracking-wide">Pawkt signal</span>
      </div>
      <p className="font-bold text-sm" style={{ color: "#1F1B18" }}>{kind}</p>
      {windowTxt && <p className="mt-1 text-[13px]" style={{ color: "#7B6D62" }}>{windowTxt}</p>}
      {conf && <p className="mt-0.5 text-[12px]" style={{ color: "#B5A89D" }}>Confidence: {conf}</p>}
      <div className="mt-auto pt-4 flex gap-2">
        <button onClick={() => nav(p.customer_id ? `/customers/${p.customer_id}` : "/follow-ups")}
          data-testid={`cc-signal-review-${p.id}`}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-bold"
          style={{ background: "#7C5CFC", color: "#fff" }}>
          Review <ArrowRight size={14} />
        </button>
        <button onClick={() => nav("/follow-ups")} data-testid={`cc-signal-followup-${p.id}`}
          className="rounded-full px-3.5 py-1.5 text-[13px] font-bold"
          style={{ background: "#FFFFFF", color: "#5837D8", border: "1px solid #B8A5FD" }}>
          Create follow-up
        </button>
      </div>
    </article>
  );
}

function OperationsBand({ ops, nav }) {
  const wf = ops?.workflow || {};
  const jn = ops?.journey || {};
  const wfShow = wf.enabled && ((wf.groups || []).length || (wf.attention || []).length);
  const jnShow = jn.enabled && ((jn.groups || []).length || (jn.attention || []).length);
  if (!wfShow && !jnShow) return null;
  const attention = [
    ...(wf.attention || []).map((a) => ({ ...a, label: a.workflow, kind: "workflow" })),
    ...(jn.attention || []).map((a) => ({ ...a, label: a.journey, kind: "journey" })),
  ];
  return (
    <Band id="operations" title="In Progress" Icon={Route} muted>
      {attention.length > 0 && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2" data-testid="cc-ops-attention">
          {attention.map((a, i) => (
            <article key={i} data-testid={`cc-ops-attn-${i}`} className="rounded-[18px] p-5 flex flex-col"
              style={{ background: "#FFFFFF", border: "1px solid #EEE8DD", borderLeft: "4px solid #C8482A" }}>
              <div className="flex items-center gap-1.5 mb-2" style={{ color: "#C8482A" }}>
                <AlertTriangle size={14} />
                <span className="text-[11px] font-extrabold uppercase tracking-wide">Needs a look</span>
              </div>
              <h3 className="font-bold text-sm" style={{ color: "#1F1B18" }}>{a.label}</h3>
              <p className="mt-1 text-[13px]" style={{ color: "#7B6D62" }}>{a.reason}</p>
              <div className="mt-auto pt-4">
                <LinkBtn to={a.customer_id ? `/customers/${a.customer_id}` : "/action-center"}
                  testid={`cc-ops-attn-review-${i}`} variant="solid">Review <ArrowRight size={14} /></LinkBtn>
              </div>
            </article>
          ))}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cc-ops-groups">
        {(wf.groups || []).map((g, i) => (
          <button key={`w${i}`} data-testid={`cc-ops-wf-${i}`}
            onClick={() => nav(/boarding/i.test(g.workflow) ? "/boarding" : "/action-center")}
            className="rounded-[18px] p-5 text-left transition-colors"
            style={{ background: "#FFFFFF", border: "1px solid #EEE8DD" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B8A5FD")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#EEE8DD")}>
            <GitBranch size={18} style={{ color: "#7C5CFC" }} />
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-extrabold text-xl" style={{ color: "#1F1B18" }}>{g.count}</span>
              <span className="text-sm font-semibold" style={{ color: "#1F1B18" }}>{g.workflow}</span>
            </div>
            <p className="mt-0.5 text-[13px]" style={{ color: "#7B6D62" }}>{g.state} · in progress</p>
          </button>
        ))}
        {(jn.groups || []).map((g, i) => (
          <button key={`j${i}`} data-testid={`cc-ops-jny-${i}`} onClick={() => nav("/customers")}
            className="rounded-[18px] p-5 text-left transition-colors"
            style={{ background: "#FFFFFF", border: "1px solid #EEE8DD" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B8A5FD")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#EEE8DD")}>
            <Route size={18} style={{ color: "#7C5CFC" }} />
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-extrabold text-xl" style={{ color: "#1F1B18" }}>{g.count}</span>
              <span className="text-sm font-semibold" style={{ color: "#1F1B18" }}>{g.journey}</span>
            </div>
            <p className="mt-0.5 text-[13px]" style={{ color: "#7B6D62" }}>Active care journey</p>
          </button>
        ))}
      </div>
    </Band>
  );
}

const ActivityRow = ({ n, label, bad }) => (
  <li className="flex items-center gap-2">
    <CheckCircle2 size={15} style={{ color: bad ? "#C8482A" : "#2F8F5B" }} />
    <span><span className="font-bold">{n}</span> {label}</span>
  </li>
);

/* truthful delivery labels — MOCK/queued are NEVER shown as sent/delivered */
const DELIVERY = {
  mocked: { t: "processed via preview/mock", c: "#5837D8" },
  mock: { t: "processed via preview/mock", c: "#5837D8" },
  queued: { t: "queued", c: "#B8791F" },
  suppressed: { t: "suppressed", c: "#C8482A" },
  skipped: { t: "skipped (duplicate prevented)", c: "#7B6D62" },
  failed: { t: "failed", c: "#C8482A" },
  sent: { t: "sent (provider-confirmed)", c: "#2F8F5B" },
  delivered: { t: "delivered (provider-confirmed)", c: "#2F8F5B" },
};
function DeliveryBreakdown({ counts, suppressed }) {
  const entries = Object.entries(counts || {}).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return (
    <div className="mt-4 pt-3" style={{ borderTop: "1px solid #F0E8DA" }} data-testid="cc-delivery-breakdown">
      <p className="text-[11px] font-extrabold uppercase tracking-wide mb-2" style={{ color: "#7B6D62" }}>Communications</p>
      <ul className="space-y-1 text-[13px]">
        {entries.map(([status, n]) => {
          const d = DELIVERY[status] || { t: humanize(status), c: "#7B6D62" };
          return (
            <li key={status} className="flex items-center gap-2" style={{ color: "#1F1B18" }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: d.c }} aria-hidden />
              <span className="font-bold">{n}</span> {d.t}
            </li>
          );
        })}
      </ul>
      {(suppressed || []).length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[12px]" style={{ color: "#7B6D62" }}>
          {suppressed.slice(0, 3).map((s, i) => (
            <li key={i}>• {humanize(s.channel)} suppressed — {humanize(s.reason)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
